import { FileArchive, FileText, RotateCcw, Search, Upload } from 'lucide-react'
import * as React from 'react'
import { ClientOnly } from 'remix-utils/client-only'
import { DocxFileViewer, type DocumentViewerHandle, PdfFileViewer } from '#app/components/document-viewers.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { cn } from '#app/utils/misc.tsx'

type PlaygroundFile = {
	name: string
	type: 'pdf' | 'docx'
	url: string
	size: number
}

export const meta = () => [{ title: 'Viewer Playground | IUS PRO' }]

export default function ViewerPlaygroundRoute() {
	return <ClientOnly fallback={<PlaygroundShellFallback />}>{() => <ViewerPlayground />}</ClientOnly>
}

function ViewerPlayground() {
	const viewerRef = React.useRef<DocumentViewerHandle>(null)
	const fileInputRef = React.useRef<HTMLInputElement>(null)
	const [file, setFile] = React.useState<PlaygroundFile | null>(null)
	const [highlightText, setHighlightText] = React.useState('')
	const [highlightStatus, setHighlightStatus] = React.useState<'idle' | 'found' | 'missing'>('idle')
	const [dragActive, setDragActive] = React.useState(false)

	React.useEffect(() => {
		return () => {
			if (file?.url) URL.revokeObjectURL(file.url)
		}
	}, [file?.url])

	function loadFile(nextFile: File) {
		const lowerName = nextFile.name.toLowerCase()
		const type = lowerName.endsWith('.pdf') ? 'pdf' : lowerName.endsWith('.docx') ? 'docx' : null
		if (!type) {
			setHighlightStatus('missing')
			return
		}

		if (file?.url) URL.revokeObjectURL(file.url)
		setFile({
			name: nextFile.name,
			type,
			url: URL.createObjectURL(nextFile),
			size: nextFile.size,
		})
		setHighlightText('')
		setHighlightStatus('idle')
	}

	function highlightCitation() {
		const text = highlightText.trim()
		if (!text || !viewerRef.current) return
		const found = viewerRef.current.highlightCitation(text)
		setHighlightStatus(found ? 'found' : 'missing')
	}

	function resetPlayground() {
		if (file?.url) URL.revokeObjectURL(file.url)
		setFile(null)
		setHighlightText('')
		setHighlightStatus('idle')
		if (fileInputRef.current) fileInputRef.current.value = ''
	}

	return (
		<main className="min-h-screen bg-[#f5f2ec] text-stone-950">
			<div className="mx-auto flex min-h-screen w-full max-w-[1560px] flex-col px-4 py-5 sm:px-6 lg:px-8">
				<header className="flex flex-col gap-5 border-b border-stone-300/70 pb-5 lg:flex-row lg:items-end lg:justify-between">
					<div className="max-w-2xl">
						<p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-500">
							IUS PRO document viewer
						</p>
						<h1 className="mt-3 text-3xl font-semibold tracking-normal text-stone-950 sm:text-4xl">
							Viewer Playground
						</h1>
					</div>
					<div className="flex flex-wrap items-center gap-2">
						<Button
							type="button"
							variant="outline"
							className="gap-2 rounded-full border-stone-300 bg-[#fbfaf7] text-stone-700"
							onClick={() => fileInputRef.current?.click()}
						>
							<Upload className="h-4 w-4" />
							Subir documento
						</Button>
						<Button
							type="button"
							variant="ghost"
							className="gap-2 rounded-full text-stone-600 hover:bg-stone-200/60"
							onClick={resetPlayground}
							disabled={!file}
						>
							<RotateCcw className="h-4 w-4" />
							Limpiar
						</Button>
					</div>
				</header>

				<section className="grid min-h-0 flex-1 gap-5 py-5 xl:grid-cols-[380px_minmax(0,1fr)]">
					<aside className="flex min-h-0 flex-col gap-4">
						<label
							className={cn(
								'group relative flex min-h-56 cursor-pointer flex-col justify-between overflow-hidden rounded-[22px] border border-dashed bg-[#fbfaf7] p-5 shadow-sm transition-all',
								dragActive
									? 'border-stone-800 ring-4 ring-stone-900/10'
									: 'border-stone-300 hover:border-stone-500 hover:bg-white',
							)}
							onDragEnter={event => {
								event.preventDefault()
								setDragActive(true)
							}}
							onDragOver={event => {
								event.preventDefault()
								setDragActive(true)
							}}
							onDragLeave={event => {
								event.preventDefault()
								setDragActive(false)
							}}
							onDrop={event => {
								event.preventDefault()
								setDragActive(false)
								const droppedFile = event.dataTransfer.files.item(0)
								if (droppedFile) loadFile(droppedFile)
							}}
						>
							<input
								ref={fileInputRef}
								type="file"
								accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
								className="sr-only"
								onChange={event => {
									const selectedFile = event.currentTarget.files?.item(0)
									if (selectedFile) loadFile(selectedFile)
								}}
							/>
							<div className="flex items-start justify-between gap-3">
								<div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-stone-950 text-[#fbfaf7] shadow-sm">
									<Upload className="h-5 w-5" />
								</div>
								<span className="rounded-full border border-stone-200 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.12em] text-stone-500">
									PDF / DOCX
								</span>
							</div>
							<div>
								<p className="text-lg font-semibold text-stone-950">Arrastrá un documento</p>
								<p className="mt-2 text-sm leading-6 text-stone-500">
									El archivo se mantiene local en el navegador para probar el render y las citas.
								</p>
							</div>
						</label>

						<div className="rounded-[22px] border border-stone-200 bg-[#fbfaf7] p-5 shadow-sm">
							<div className="flex items-center gap-3">
								<div className="flex h-10 w-10 items-center justify-center rounded-xl bg-stone-100 text-stone-700">
									{file?.type === 'docx' ? <FileText className="h-5 w-5" /> : <FileArchive className="h-5 w-5" />}
								</div>
								<div className="min-w-0">
									<p className="truncate text-sm font-semibold text-stone-900">{file?.name ?? 'Sin documento'}</p>
									<p className="text-xs text-stone-500">{file ? formatBytes(file.size) : 'Subí un PDF o DOCX'}</p>
								</div>
							</div>
						</div>

						<form
							className="rounded-[22px] border border-stone-200 bg-[#fbfaf7] p-5 shadow-sm"
							onSubmit={event => {
								event.preventDefault()
								highlightCitation()
							}}
						>
							<label htmlFor="citation-text" className="text-sm font-semibold text-stone-900">
								Texto de cita
							</label>
							<textarea
								id="citation-text"
								value={highlightText}
								onChange={event => {
									setHighlightText(event.currentTarget.value)
									setHighlightStatus('idle')
								}}
								placeholder="Pegá una palabra, frase, oración o párrafo..."
								className="mt-3 min-h-36 w-full resize-none rounded-2xl border border-stone-200 bg-white px-4 py-3 text-sm leading-6 text-stone-900 outline-none transition focus:border-stone-600 focus:ring-4 focus:ring-stone-900/10"
							/>
							<div className="mt-3 flex items-center justify-between gap-3">
								<p
									className={cn(
										'text-xs',
										highlightStatus === 'found' && 'text-emerald-700',
										highlightStatus === 'missing' && 'text-red-600',
										highlightStatus === 'idle' && 'text-stone-500',
									)}
								>
									{highlightStatus === 'found'
										? 'Cita localizada'
										: highlightStatus === 'missing'
											? 'No se encontró coincidencia'
											: 'Listo para buscar'}
								</p>
								<Button type="submit" className="gap-2 rounded-full" disabled={!file || !highlightText.trim()}>
									<Search className="h-4 w-4" />
									Buscar
								</Button>
							</div>
						</form>
					</aside>

					<section className="min-h-[560px] overflow-hidden rounded-[28px] border border-stone-200 bg-[#e9e5dd] p-3 shadow-sm">
						{file ? (
							file.type === 'pdf' ? (
								<PdfFileViewer ref={viewerRef} url={file.url} title={file.name} className="h-[calc(100vh-10rem)]" />
							) : (
								<DocxFileViewer ref={viewerRef} url={file.url} title={file.name} className="h-[calc(100vh-10rem)]" />
							)
						) : (
							<div className="flex h-full min-h-[560px] items-center justify-center rounded-[22px] border border-stone-200/80 bg-[#f1eee8]">
								<div className="max-w-sm px-6 text-center">
									<div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#fbfaf7] text-stone-700 shadow-sm">
										<FileText className="h-6 w-6" />
									</div>
									<p className="text-lg font-semibold text-stone-900">La vista previa aparece aquí</p>
									<p className="mt-2 text-sm leading-6 text-stone-500">
										Subí un archivo y probá la búsqueda exacta sobre el texto renderizado.
									</p>
								</div>
							</div>
						)}
					</section>
				</section>
			</div>
		</main>
	)
}

function PlaygroundShellFallback() {
	return (
		<main className="flex min-h-screen items-center justify-center bg-[#f5f2ec] text-stone-600">
			<div className="rounded-full border border-stone-200 bg-[#fbfaf7] px-4 py-2 text-sm shadow-sm">
				Preparando playground...
			</div>
		</main>
	)
}

function formatBytes(bytes: number) {
	if (bytes < 1024) return `${bytes} B`
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
